import { Filter, filterToRawExpression, isTopKFilter } from '@fabric-ui/components';
import { isNullOrUndefined } from '@utils/utils';
import { groupOthersSizeMax, interactiveQueryMaxRowsSize } from '../../../types/QueryObject.types';

/**
 * Validates the "Show Top / Page Size" numeric input.
 *
 * @param size - The raw value from the input (null / undefined = empty field).
 * @param groupOthers - Whether the "Group Others" checkbox is currently checked.
 * @param sizeLabel - The label shown in error messages ("Show Top" or "Page Size").
 * @returns An error message string, or null when the value is valid.
 */
export const validateTopSize = (size: number | null | undefined, groupOthers: boolean, sizeLabel: string): string | null => {
  const rangeError = `${sizeLabel} value must be between 1 - ${interactiveQueryMaxRowsSize.toLocaleString()}`;
  const groupOthersError = `When Group Others is selected, ${sizeLabel} value must be between 1 - ${groupOthersSizeMax}`;

  if (isNullOrUndefined(size)) {
    return groupOthers ? groupOthersError : rangeError;
  }

  if (groupOthers && (size < 1 || size > groupOthersSizeMax)) {
    return groupOthersError;
  }

  if (size < 1) {
    return rangeError;
  }

  if (size > interactiveQueryMaxRowsSize) {
    return `${sizeLabel} value can be up to ${interactiveQueryMaxRowsSize.toLocaleString()}`;
  }

  return null;
};

/**
 * Generic function to traverse a filter tree and apply a predicate to remove matching expressions
 * @param filter - The filter to traverse
 * @param shouldRemove - Predicate function that returns true if the expression should be removed
 * @returns The filtered Filter object, or null if all expressions were removed
 */
export const filterFilterBy = (filter: Filter, shouldRemove: (filter: Filter) => boolean, preserveWrapper = false): Filter | null => {
  if (!filter) {
    return null;
  }

  if (filter.expression) {
    if (shouldRemove(filter)) {
      return null; // Remove this expression
    }
    return filter; // Keep this expression
  }

  if (filter.and?.operands) {
    const newOperands = filter.and.operands.map(operand => filterFilterBy(operand, shouldRemove, false)).filter(Boolean) as Filter[]; // Filter out nulls

    if (newOperands.length === 0) {
      return null;
    }

    // Always preserve wrapper at top level to maintain expected structure for NewExpressionBuilder
    if (newOperands.length === 1 && !preserveWrapper) {
      return newOperands[0];
    }
    return { and: { operands: newOperands } };
  }

  if (filter.or?.operands) {
    const newOperands = filter.or.operands.map(operand => filterFilterBy(operand, shouldRemove, false)).filter(Boolean) as Filter[]; // Filter out nulls

    if (newOperands.length === 0) {
      return null;
    }

    // Always preserve wrapper at top level to maintain expected structure for NewExpressionBuilder
    if (newOperands.length === 1 && !preserveWrapper) {
      return newOperands[0];
    }
    return { or: { operands: newOperands } };
  }

  return filter;
};

export const removeTopKFilters = (filter: Filter): Filter | null =>
  filterFilterBy(
    filter,
    f => {
      if (!f?.expression) return false;
      const rawExp = filterToRawExpression(f);
      return isTopKFilter(rawExp?.type || '');
    },
    true // Preserve wrapper to maintain structure expected by NewExpressionBuilder
  );

export const hasTopKFilter = (filter: Filter): boolean => {
  if (!filter) return false;

  if (filter.expression) {
    const rawExp = filterToRawExpression(filter);
    return isTopKFilter(rawExp?.type || '');
  }

  if (filter.and?.operands) {
    return filter.and.operands.some(operand => hasTopKFilter(operand));
  }

  if (filter.or?.operands) {
    return filter.or.operands.some(operand => hasTopKFilter(operand));
  }

  return false;
};
